import { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronDown, Check, Clock } from 'lucide-react';
import '../../styles/components/date-range.scss';

const DateRangeSelector = ({ onChange, clearTrigger }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState('Today');
  const [fromDate, setFromDate] = useState(formatDate(new Date()));
  const [toDate, setToDate] = useState(formatDate(new Date()));
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');

  const dropdownRef = useRef(null);

  function formatDate(date) {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };
    return date.toLocaleDateString('en-US', options);
  }

  function formatInputDate(date) {
    return date.toISOString().split('T')[0];
  }

  function getDateRange(option) {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const last7Days = new Date(today);
    last7Days.setDate(last7Days.getDate() - 7);

    const last15Days = new Date(today);
    last15Days.setDate(last15Days.getDate() - 15);

    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    switch (option) {
      case 'Today':
        return { from: today, to: today };
      case 'Yesterday':
        return { from: yesterday, to: yesterday };
      case 'Last 7 Days':
        return { from: last7Days, to: today };
      case 'Last 15 Days':
        return { from: last15Days, to: today };
      case 'Last Month':
        return { from: lastMonth, to: today };
      default:
        return { from: today, to: today };
    }
  }
  useEffect(() => {
    if (clearTrigger !== null) {
      const today = new Date();

      setSelectedOption('Today');
      setFromDate(formatDate(today));
      setToDate(formatDate(today));
      setCustomFromDate('');
      setCustomToDate('');

      if (onChange) {
        onChange({
          fromDate: formatInputDate(today),
          toDate: formatInputDate(today),
          option: 'Today',
        });
      }
    }
  }, [clearTrigger, onChange]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleOptionSelect = (option) => {
    if (option === 'Custom') {
      setSelectedOption(option);
      setIsOpen(false);
    } else {
      const { from, to } = getDateRange(option);
      setSelectedOption(option);
      setFromDate(formatDate(from));
      setToDate(formatDate(to));
      setIsOpen(false);

      if (onChange) {
        onChange({
          fromDate: formatInputDate(from),
          toDate: formatInputDate(to),
          option: option,
        });
      }
    }
  };

  const applyCustomDates = () => {
    if (customFromDate && customToDate) {
      const fromDateObj = new Date(customFromDate);
      const toDateObj = new Date(customToDate);

      setFromDate(formatDate(fromDateObj));
      setToDate(formatDate(toDateObj));
      setSelectedOption('Custom Range');
      setIsOpen(false);

      if (onChange) {
        onChange({
          fromDate: customFromDate,
          toDate: customToDate,
          option: 'Custom Range',
        });
      }
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const options = [
    { key: 'Today', label: 'Today', date: formatDate(new Date()), icon: Calendar },
    { key: 'Yesterday', label: 'Yesterday', date: formatDate(new Date(Date.now() - 86400000)), icon: Clock },
    {
      key: 'Last 7 Days',
      label: 'Last 7 Days',
      date: formatDate(new Date(Date.now() - 7 * 86400000)) + ' - ' + formatDate(new Date()),
      icon: Calendar,
    },
    {
      key: 'Last 15 Days',
      label: 'Last 15 Days',
      date: formatDate(new Date(Date.now() - 15 * 86400000)) + ' - ' + formatDate(new Date()),
      icon: Calendar,
    },
    {
      key: 'Last Month',
      label: 'Last Month',
      date: formatDate(new Date(new Date().setMonth(new Date().getMonth() - 1))) + ' - ' + formatDate(new Date()),
      icon: Calendar,
    },
  ];

  return (
    <>
      <style>{`
        
      `}</style>

      <div className="pro-date-selector" ref={dropdownRef}>
        <div className={`pro-date-input ${isOpen ? 'pro-date-input--open' : ''}`} onClick={toggleDropdown}>
          <Calendar className="pro-calendar-icon" size={18} />
          <div className="pro-date-display">{fromDate === toDate ? fromDate : `${fromDate} → ${toDate}`}</div>
          <ChevronDown className={`pro-dropdown-arrow ${isOpen ? 'pro-dropdown-arrow--open' : ''}`} size={20} />
        </div>

        {isOpen && (
          <div className="pro-dropdown-menu">
            <div className="pro-dropdown-header">Date Range Selection</div>

            {options.map((option) => {
              const IconComponent = option.icon;
              return (
                <div
                  key={option.key}
                  className={`pro-dropdown-option ${
                    selectedOption === option.key ? 'pro-dropdown-option--selected' : ''
                  }`}
                  onClick={() => handleOptionSelect(option.key)}
                >
                  <div className="pro-option-content">
                    <IconComponent className="pro-option-icon" size={16} />
                    <div>
                      <div className="pro-option-label">{option.label}</div>
                      <div className="pro-option-date">{option.date}</div>
                    </div>
                    {selectedOption === option.key && <Check className="pro-check-icon" size={16} />}
                  </div>
                </div>
              );
            })}

            <div className="pro-custom-section">
              <div className="pro-custom-label">Custom Date Range</div>
              <div className="pro-custom-inputs">
                <input
                  type="date"
                  className="pro-custom-input"
                  placeholder="From Date"
                  value={customFromDate}
                  onChange={(e) => setCustomFromDate(e.target.value)}
                />
                <input
                  type="date"
                  className="pro-custom-input"
                  placeholder="To Date"
                  value={customToDate}
                  onChange={(e) => setCustomToDate(e.target.value)}
                />
              </div>
              <button
                className="pro-apply-button"
                onClick={applyCustomDates}
                disabled={!customFromDate || !customToDate}
              >
                Apply Custom Range
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DateRangeSelector;
